import _ from 'lodash'
import config from '@/config'
import Cookie from '@/common/utils/cookies'

const isBrowser = (process.browser || process.type === 'renderer')

let Vue

function isDataExpired () {
  return [].slice.call(arguments).map(arg => {
    return typeof arg === 'object' && Object.prototype.hasOwnProperty.call(arg, 'dataState') && arg.dataState === 'expired'
  }).reduce((a, b) => {
    return a || b
  }, false)
}

function install (_Vue) {
  if (Vue) {
    console.error(
      '[api] already installed.'
    )
    return
  }

  Vue = _Vue

  Vue.prototype.$imagePath = function(value) {
    if (value.startsWith('/')) {
      return config.host + value
    }
    if (value.startsWith('http')) {
      return value
    }
    if (value) {
      return config.host + '/images/' + value[0] + value[1] + '/' + value[2] + value[3] + '/' + value.substr(4)
    }
  }
  Vue.prototype.$publicPath = function(value) {
    return config.publicPath + value
  }

  Vue.prototype.$getDataState = function(state) {
    switch (state) {
      case 'loading':
        return { dataState: state }
    }
  }

  Vue.prototype.$isDataLoaded = function() {
    return [].slice.call(arguments).map(arg => {
      return !(arg && Object.prototype.hasOwnProperty.call(arg, 'dataState')) || (arg.dataState === 'empty') || (arg.dataState.indexOf('expired') === 0)
    }).reduce((a, b) => {
      return a && b
    }, true)
  }

  Vue.prototype.$isDataLoading = function() {
    return [].slice.call(arguments).map(arg => {
      return typeof arg === 'object' && Object.prototype.hasOwnProperty.call(arg, 'dataState') &&
             (arg.dataState === 'loading' || arg.dataState === 'expired-loading')
    }).reduce((a, b) => {
      return a || b
    }, false)
  }

  Vue.prototype.$isDataLoadFailed = function() {
    return [].slice.call(arguments).map(arg => {
      return typeof arg === 'object' && Object.prototype.hasOwnProperty.call(arg, 'dataState') && arg.dataState === 'error'
    }).reduce((a, b) => {
      return a || b
    }, false)
  }

  Vue.prototype.$isDataEmpty = function() {
    return [].slice.call(arguments).map(arg => {
      return arg && typeof arg === 'object' && Object.prototype.hasOwnProperty.call(arg, 'dataState') && arg.dataState === 'empty'
    }).reduce((a, b) => {
      return a && b
    }, true)
  }

  Vue.mixin({
    beforeCreate: function() {
      const options = this.$options
      // store injection
      if (options.apiData) {
        this.$api = options.apiData
      } else if (options.parent && options.parent.$api) {
        this.$api = options.parent.$api
      }
    }
  })

  // Vue.prototype.$api = new CachedApi(Vue)
}

class RestApi {
  constructor (axios) {
    this.axios = axios
  }

  _parseError (err) {
    if (err.response) {
      const response = err.response
      if (Object.prototype.hasOwnProperty.call(response, 'data') &&
          response.data &&
          Object.prototype.hasOwnProperty.call(response.data, 'data')) {
        const error = new Error(response.data.data.message)
        if (response.data.data.code === 401 && response.data.data.message === 'TokenInvalid' && isBrowser) {
          // token 已失效
          Cookie.remove('token')
          Cookie.setLocal('redirect_message', '登录信息已失效，请重新登录')
          window.location.href = '/'
          return Promise.reject(error)
        }
        if (response.data.data.code === 422) {
          if (typeof response.data.data.message === 'object') {
            error.message = Object.values(response.data.data.message).join(',')
          } else {
            error.message = response.data.data.message.toString()
          }
        }
        error.data = response.data.data
        error.code = response.data.data.code
        return Promise.reject(error)
      }
      return Promise.reject(err)
    } else {
      return Promise.reject(new Error('请检查您的网络连接是否正常'))
    }
  }

  async getRaw (path, options = {}) {
    try {
      let response = await this.axios.get(`${config.api}${path}`, options)
      return Promise.resolve(response.data)
    } catch (err) {
      return this._parseError(err)
    }
  }

  async get (path, options = {}) {
    try {
      let response = await this.axios.get(`${config.api}${path}`, options)
      return (await Promise.resolve(response.data.data))
    } catch (err) {
      return this._parseError(err)
    }
  }

  async patch (path, body = {}, options = {}) {
    try {
      if (!Object.prototype.hasOwnProperty.call(options, 'headers')) {
        options.headers = {}
      }
      options.headers['Content-Type'] = 'application/json'

      let response = await this.axios.patch(`${config.api}${path}`, body, options)
      return Promise.resolve(response.data.data)
    } catch (err) {
      return this._parseError(err)
    }
  }

  async put (path, body = {}, options = {}) {
    try {
      if (!Object.prototype.hasOwnProperty.call(options, 'headers')) {
        options.headers = {}
      }
      options.headers['Content-Type'] = 'application/json'

      let response = await this.axios.put(`${config.api}${path}`, body, options)
      return Promise.resolve(response.data.data)
    } catch (err) {
      return this._parseError(err)
    }
  }

  async post (path, body = {}, options = {}) {
    try {
      if (!Object.prototype.hasOwnProperty.call(options, 'headers')) {
        options.headers = {}
      }
      options.headers['Content-Type'] = 'application/json'
      let response = await this.axios.post(`${config.api}${path}`, body, options)
      return Promise.resolve(response.data.data)
    } catch (err) {
      return this._parseError(err)
    }
  }

  async 'delete' (path, options = {}) {
    try {
      let response = await this.axios.delete(`${config.api}${path}`, options)
      return Promise.resolve(response.data.data)
    } catch (err) {
      return this._parseError(err)
    }
  }

  setToken (token) {
    this.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
  }
}

class CachedApi extends RestApi {
  constructor (axios) {
    super(axios)
    this._vm = new Vue({
      data: { state: {} }
    })
    this.axios = axios
    this.data = this._vm.state
  }

  _storeApiData (name, callback) {
    const api = this
    if (callback instanceof Function) {
      return callback().then(function(data) {
        if (data === null) {
          data = { 'dataState': 'empty' }
        }
        Vue.set(api.data, name, data)
      }).catch((err) => {
        Vue.set(api.data, name, { 'dataState': 'error' })
        return Promise.reject(err)
      })
    } else {
      Vue.set(api.data, name, callback)
    }
  }

  _deleteApiData (name, byPrefix = false) {
    if (!name) return
    for (let prop in this.data) {
      if (Object.prototype.hasOwnProperty.call(this.data, prop) && _.startsWith(prop, name)) {
        if (!byPrefix && prop.length > name.length && prop[name.length] === '/') continue
        Vue.set(this.data[prop], 'dataState', 'expired')
      }
    }
  }

  replaceData (data) {
    this._vm = new Vue({
      data: { state: data }
    })
    this.data = this._vm.state
  }

  clearCache () {
    for (let prop in this.data) {
      if (Object.prototype.hasOwnProperty.call(this.data, prop)) {
        Vue.set(this.data, prop, { 'dataState': 'expired' })
      }
    }
  }

  expireCache (path, recursive = true) {
    if (!recursive) {
      this._deleteApiData(path)
      return Promise.resolve(true)
    }
    while (path) {
      this._deleteApiData(path)
      path = path.substr(0, path.lastIndexOf('/'))
    }
    return Promise.resolve(true)
  }

  emptyCache (path) {
    Vue.set(this.data, path, { dataState: 'expired' })
  }

  expireCacheByPrefix (prefix, recursive = true) {
    if (!recursive) {
      this._deleteApiData(prefix, true)
      return Promise.resolve(true)
    }

    while (prefix) {
      this._deleteApiData(prefix, true)
      prefix = prefix.substr(0, prefix.lastIndexOf('/'))
    }
    return Promise.resolve(true)
  }

  set (path, data) {
    Vue.set(this.data, path, data)
  }

  getPagedData (name, params, offset, limit, preFetch = false) {
    const restGet = super.getRaw
    const api = this

    let done, failed
    let promise = new Promise((resolve, reject) => {
      done = resolve
      failed = reject
    })

    let haveUndefinedParams = false
    name = name.replace(/:([a-zA-Z_]+)/g, (match, $1) => {
      if (typeof params === 'undefined' || typeof params[$1] === 'undefined' || params[$1] === null) {
        haveUndefinedParams = true
      } else {
        return params[$1]
      }
    })

    let path = name
    name = name + '?paged:' + offset + ':' + limit + ':'

    if (haveUndefinedParams) {
      promise.dataState = 'error'
      done({ dataState: 'error' })
      return promise
    }

    // 在服务端渲染时 computed 中部分数据没有在预取 asyncData 时获得
    // 因此需要将这些数据设为 expired
    // 客户端加载时才会重新请求
    const isSSR = !process.browser && process.type !== 'renderer'
    const isDataNull = typeof this.data[name] === 'undefined' || this.data[name] === null
    if (!preFetch && isSSR && isDataNull) {
      promise.dataState = 'expired'
      done({ dataState: 'expired' })
      return promise
    }

    // 当缓存中 (数据为空 | 数据过期 | 分页数据变化) 时重新请求数据
    if (isDataNull || isDataExpired(this.data[name]) ||
        // paging params changed
        (this.data[name] && this.data[name].meta && (this.data[name].meta.offset !== offset || (this.data[name].meta.limit !== limit)))
    ) {
      promise.dataState = 'loading'
      api._storeApiData(name, promise)

      if (path.indexOf('?') === -1) {
        path += '?'
      } else if (!_.endsWith(path, '?')) {
        path += '&'
      }

      path += 'paging=' + encodeURIComponent(JSON.stringify({ 'offset': offset, 'limit': limit }))

      restGet.call(this, path, {}).then(data => {
        data.meta.offset = offset
        data.meta.limit = limit
        api._storeApiData(name, data)
        done(data)
      }).catch(() => {
        api._storeApiData(name, { dataState: 'error' })
        failed({ dataState: 'error' })
      })
    }

    return this.data[name]
  }

  getDataEx (name, params, offset, limit, sort = {}, filter = {}, preFetch = false) {
    sort = encodeURIComponent(JSON.stringify(sort))

    filter = _.clone(filter)

    for (let key of Object.keys(filter)) {
      if ((!_.isNumber(filter[key]) && !filter[key])) {
        delete filter[key]
      }
    }
    filter = encodeURIComponent(JSON.stringify(filter))

    if (name.indexOf('?') !== -1) {
      name += `&sort=${sort}&filter=${filter}`
    } else {
      name += `?sort=${sort}&filter=${filter}`
    }
    return this.getPagedData(name, params, offset, limit, preFetch)
  }

  getData (name, params, preFetch = false) {
    const restGet = super.get
    const api = this

    let done
    let fail
    let promise = new Promise((resolve, reject) => {
      done = resolve
      fail = reject
    })

    let haveUndefinedParams = false
    name = name.replace(/:([a-zA-Z_]+)/g, (match, $1) => {
      if (typeof params === 'undefined' || typeof params[$1] === 'undefined' || params[$1] === null) {
        haveUndefinedParams = true
      } else {
        return params[$1]
      }
    })

    if (haveUndefinedParams) {
      promise.dataState = 'error'
      done({ dataState: 'error' })
      return promise
    }

    // 在服务端渲染时 computed 中部分数据没有在预取 asyncData 时获得
    // 因此需要将这些数据设为 expired
    // 客户端加载时才会重新请求
    const isSSR = !process.browser && process.type !== 'renderer'
    const isDataNull = typeof this.data[name] === 'undefined' || this.data[name] === null
    if (!preFetch && isSSR && isDataNull) {
      promise.dataState = 'expired'
      done({ dataState: 'expired' })
      return promise
    }

    // 当缓存中 (数据为空 | 数据过期) 时重新请求数据
    if (isDataNull || isDataExpired(this.data[name])) {
      if (!isDataNull) {
        promise = this.data[name]
        Object.defineProperty(promise, 'dataState', {
          value: 'expired-loading',
          enumerable: false,
          writable: true
        })
      } else {
        promise.dataState = 'loading'
      }
      api._storeApiData(name, promise)

      restGet.call(this, name).then(data => {
        if (!data) {
          data = { dataState: 'empty' }
        }
        api._storeApiData(name, data)
        done(data)
      }).catch(e => {
        api._storeApiData(name, { dataState: 'error', message: e.message })
        fail({ dataState: 'error', message: e.message, code: e.code })
      })
    }
    return this.data[name]  // is a extended promise
  }

  get (path, options, save = false) {
    const restGet = super.get
    return new Promise((resolve, reject) => {
      restGet.call(this, path, options).then(data => {
        save && this._storeApiData(path, data)
        return resolve(data)
      }).catch(e => {
        reject(e)
      })
    })
  }

  getRaw (path, options) {
    const getRaw = super.getRaw
    return getRaw.call(this, path, options)
  }

  reload (path, options) {
    const restGet = super.get
    const api = this
    return api._storeApiData(path, () => {
      api._deleteApiData(path)
      return restGet.call(this, path, options)
    })
  }

  put (path, body = {}, options = {}, clearCache = true) {
    const api = this
    return super.put.call(this, path, body, options).then((data) => {
      if (clearCache === 'single') {
        api._deleteApiData(path)
      } else if (clearCache) {
        api.expireCache(path)
      }
      return Promise.resolve(data)
    })
  }

  post (path, body = {}, options = {}, clearCache = true) {
    const api = this
    return super.post.call(this, path, body, options).then((data) => {
      if (clearCache) {
        if (clearCache === 'single') {
          api._deleteApiData(path)
        } else if (data && data._id) {
          api.expireCache(path + '/' + data._id)
        } else {
          api.expireCache(path)
        }
      }
      return Promise.resolve(data)
    })
  }

  patch (path, body = {}, options = {}, clearCache = true) {
    const api = this
    return super.patch.call(this, path, body, options).then((data) => {
      if (clearCache === 'single') {
        api._deleteApiData(path)
      } else if (clearCache) {
        api.expireCache(path)
      }
      return Promise.resolve(data)
    })
  }

  'delete' (path, options = {}, clearCache = true) {
    const api = this
    return super.delete.call(this, path, options).then((data) => {
      if (clearCache === 'single') {
        api._deleteApiData(path)
      } else if (clearCache) {
        api.expireCache(path)
      }
      return Promise.resolve(data)
    })
  }

  async downloadFile (path, filename, part) {
    const windowReference = window.open()
    try {
      const fileIndex = await this.axios.get(`${config.api}${path}`)
      if (fileIndex.data.data) {
        windowReference.location = config.api + '/files/' + fileIndex.data.data + '/' + encodeURIComponent(filename)
        return part + 1
      } else {
        return part
      }
    } catch (e) {
      return part
    }
  }
}

export default {
  install,
  ApiData: CachedApi
}
